import styled from "styled-components";
import { Dot } from "recharts";
import { AlertTriangle } from "lucide-react";
import dayjs from "dayjs";
import "dayjs/locale/ko";

import type { ChangeRate } from "types/BusinessDashboard";

dayjs.locale("ko");

const formatCurrency = (value?: number) => `₩${(value || 0).toLocaleString()}`;

const TooltipWrapper = styled.div`
  background: #000000e5;
  padding: 10px 12px;
  border-radius: 6px;
  box-shadow: 0 4px 12px 0 rgba(0, 0, 0, 0.15);
  min-width: 164px;
`;

const TooltipDate = styled.p`
  font-size: 0.75rem;
  color: #9ca1ab;
  font-weight: 600;
  margin-bottom: 3px;
`;

const TooltipRow = styled.p`
  font-size: 0.75rem;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 2px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const TooltipLabel = styled.span`
  color: #fff;
`;

const TooltipValue = styled.span`
  color: #fff;
  font-weight: 500;
`;

const ChangeRateRow = styled.p`
  font-size: 12px;
  font-weight: 500;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const ChangeRateLabel = styled.span`
  color: #fff;
`;

const ChangeRateValue = styled.span<{ color: string }>`
  font-weight: 500;
  color: ${({ color }) => color};
`;

const ChartErrorWrapper = styled.div`
  height: 200px;
  margin-bottom: 32px;
`;

const ChartErrorInner = styled.div`
  height: 140px;
`;

const ChartErrorIcon = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;
`;

const ChartErrorTitle = styled.h2`
  font-size: 24px;
  font-weight: bold;
  color: #1f2937;
  margin-bottom: 8px;
`;

const ChartErrorDesc = styled.p`
  color: #6b7280;
`;

export const CustomizedAxisTick = (props: any) => {
  const { x, y, payload, currentYear, currentMonth } = props;
  const day = payload.value; // dataKey="day"에서 온 값 (예: 1, 2, ...)

  // dayjs를 사용하여 해당 날짜의 요일 정보 가져오기
  const date = dayjs(`${currentYear}-${currentMonth}-${day}`);
  const dayOfWeek = date.format("ddd"); // '일', '월', '화', ...

  // 요일에 따른 색상 결정
  let textColor = "#9CA1AB"; // 기본색 (월~금)
  if (dayOfWeek === "토") {
    textColor = "#7E97C8"; // 토요일: 파란색
  } else if (dayOfWeek === "일") {
    textColor = "#FE9790"; // 일요일: 빨간색
  }

  // 두 줄 텍스트를 렌더링하기 위해 <g>와 <text> 요소 사용
  return (
    <g transform={`translate(${x},${y})`}>
      <text x={0} y={0} dy={16} textAnchor="middle" fill="#70747D">
        {/* 첫 번째 줄: 날짜 */}
        <tspan x="0" dy="0" fontSize={12} fontWeight={600}>
          {day}
        </tspan>
        {/* 두 번째 줄: 요일 (dy로 줄바꿈 효과) */}
        <tspan x="0" dy="16" fill={textColor} fontSize={12} fontWeight={500}>
          {dayOfWeek}
        </tspan>
      </text>
    </g>
  );
};

// ReferenceLine 커스텀 라벨 컴포넌트
export const ReferenceLabel = (props: any) => {
  const { viewBox, value, text, bgColor, textColor, valueColor } = props;
  const { x, y } = viewBox; // 라인의 좌표

  // 라벨의 크기와 위치 오프셋 설정
  const labelWidth = 74;
  const labelHeight = 40;
  const xOffset = x - labelWidth - 4; // Y축 왼쪽에 위치
  const yOffset = y - labelHeight / 2; // 라인 중앙에 위치

  return (
    <g>
      {/* 라벨 배경 */}
      <rect
        x={xOffset}
        y={yOffset}
        rx="4"
        ry="4"
        width={labelWidth}
        height={labelHeight}
        fill={bgColor}
      />
      {/* 라벨 텍스트 (두 줄) */}
      <text
        x={xOffset + labelWidth / 2}
        y={y}
        textAnchor="middle"
        dominantBaseline="middle"
      >
        <tspan
          x={xOffset + labelWidth / 2}
          dy="-0.4em"
          fill={textColor}
          fontSize="12px"
          fontWeight="500"
        >
          {text}
        </tspan>
        <tspan
          x={xOffset + labelWidth / 2}
          dy="1.2em"
          fill={valueColor || textColor}
          fontSize="12px"
          fontWeight="500"
        >
          {value?.toLocaleString()}
        </tspan>
      </text>
    </g>
  );
};

const PrevMonthChangeRate = ({ changeRate }: { changeRate?: ChangeRate }) => {
  if (!changeRate) return null; // 데이터가 없으면 표시하지 않음

  let valueText: string;
  let color = "#6b7280"; // 기본 회색

  if (changeRate.calculable === null) {
    valueText = "0.0";
  } else if (!changeRate.calculable) {
    valueText = changeRate.positive ? "∞" : "-∞";
  } else {
    const value = changeRate.value ?? 0;
    if (value >= 0) {
      valueText = `+${value.toFixed(1)}`;
      color = "#85AEFF"; // 양수일 때 파란색
    } else if (value < 0) {
      valueText = `${value.toFixed(1)}`;
      color = "#FD4235"; // 음수일 때 빨간색
    } else {
      valueText = value.toFixed(1);
    }
  }

  return (
    <ChangeRateRow>
      <ChangeRateLabel>전월 동기간 대비</ChangeRateLabel>
      <ChangeRateValue color={color}>{valueText}%</ChangeRateValue>
    </ChangeRateRow>
  );
};

// 차트 툴팁 컴포넌트
export const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    const displayDate = data.date;
    const changeRateData = data.changeRatePrevMonth;

    // originalValue가 있으면 그 값을, 없으면 기존 value 사용
    const valueForTooltip = data.originalValue ?? payload[0].value;

    return (
      <TooltipWrapper>
        <TooltipDate>{displayDate}</TooltipDate>
        <TooltipRow>
          <TooltipLabel>{label}</TooltipLabel>
          <TooltipValue>{formatCurrency(valueForTooltip)}</TooltipValue>
        </TooltipRow>
        <PrevMonthChangeRate changeRate={changeRateData} />
      </TooltipWrapper>
    );
  }
  return null;
};

export const CustomTooltip2 = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    const valueItem = payload.find((item: any) => item.dataKey === "value");
    const data = valueItem?.payload || payload[0].payload;
    const displayDate = data.date;
    const changeRateData = data.changeRatePrevMonth;

    // originalValue가 있으면 그 값을, 없으면 기존 value 사용
    const valueForTooltip =
      data.originalValue ?? (valueItem ? valueItem.value : payload[0].value);

    return (
      <TooltipWrapper>
        <TooltipDate>{displayDate}</TooltipDate>
        <TooltipRow>
          <TooltipLabel>{label}</TooltipLabel>
          <TooltipValue>{formatCurrency(valueForTooltip)}</TooltipValue>
        </TooltipRow>
        <PrevMonthChangeRate changeRate={changeRateData} />
      </TooltipWrapper>
    );
  }
  return null;
};

// 라인 차트 마지막 점 컴포넌트
export const LastDot = (props: any) => {
  const { cx, cy, color, index, chartData } = props;
  if (chartData && index === chartData.length - 1) {
    return <Dot cx={cx} cy={cy} r={4} strokeWidth={2} fill={color} />;
  }
  return null;
};

export const ChartError = () => (
  <ChartErrorWrapper>
    <ChartErrorInner>
      <ChartErrorIcon>
        <AlertTriangle width={32} height={32} color="#dc2626" strokeWidth={2} />
      </ChartErrorIcon>
      <ChartErrorTitle>데이터를 불러올 수 없습니다</ChartErrorTitle>
      <ChartErrorDesc>
        매출 데이터를 불러오는 중 예상치 못한 오류가 발생했습니다.
      </ChartErrorDesc>
    </ChartErrorInner>
  </ChartErrorWrapper>
);

const StyledCard = styled.div`
  border-radius: 0;
  border: none;
  box-shadow: none;
  padding: 0 16px;
  gap: 0;
  background: #fff;
`;

const StyledCardHeader = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding-left: 0;
  padding-right: 0;
  margin-bottom: 12px;
`;

const StyledCardTitle = styled.div`
  font-size: 16px;
  font-weight: 700;
  line-height: 24px;
  letter-spacing: -0.2px;
  color: #363e52;
  padding: 0;
`;

const StyledCardContent = styled.div`
  padding: 0;
`;

const ScrollableChartWrapper = styled.div`
  width: 100%;
  overflow-x: auto;
`;

const ChartInner = styled.div`
  width: 100%;
  height: 200px;
  position: relative;
  display: flex;
  align-items: flex-end;
  justify-content: flex-start;
`;

export function ChartSkeleton({ title = "평균 판매가격" }: { title?: string }) {
  return (
    <StyledCard>
      <StyledCardHeader>
        <StyledCardTitle>{title}</StyledCardTitle>
      </StyledCardHeader>
      <StyledCardContent>
        <ScrollableChartWrapper>
          <ChartInner>{/* Y축 그리드 라인 (3줄) */}</ChartInner>
        </ScrollableChartWrapper>
      </StyledCardContent>
    </StyledCard>
  );
}
